// S33 Backup & move (Implementation Plan v02 §6.4; IR-24, IR-26; designed in
// code first, D-80; I-1a Batch 6b, IP2-36 (d)).
//
// - What a backup file is, the quiet reminder line (S33 only for now; the Home
//   line waits for the real Home, I-1b), the backup passphrase typed twice
//   (≥ 12 characters or 5 words, then the app-lock refusals), one button.
// - "Making the file…" is drawn before Argon2id runs (main thread, IP2-29 (4)).
// - The file is handed to the browser as a download (Android); the day is noted
//   in the install record. The typed passphrase is cleared afterwards.
// - The honest lines: restore only in the installed app at its own address;
//   who can open the file; "Delete everything" does not reach saved files.

import { type FormEvent, useEffect, useRef, useState } from 'react'

import { type BackupReminder, checkBackupPassphrase } from '../export'
import type { EncryptedStore, LockSession } from '../lock'
import { nextFrame } from '../ui/nextFrame'
import { saveFile } from '../ui/saveFile'
import { ScreenTitle } from '../ui/ScreenTitle'
import { SecretField } from '../ui/SecretField'

import { backup, common } from './copy'

interface BackupAndMoveProps {
  readonly session: LockSession
  readonly store: EncryptedStore
  readonly onBack: () => void
}

export function BackupAndMove({ session, store, onBack }: BackupAndMoveProps): React.JSX.Element {
  const [reminder, setReminder] = useState<BackupReminder>(null)
  const [value, setValue] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [made, setMade] = useState(false)
  // Bumped after a file is made, so the reminder line is read again.
  const [madeCount, setMadeCount] = useState(0)

  // False once Hide or a lock has removed this screen: a file finished after
  // that is dropped, never downloaded.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    session.preload()
  }, [session])

  useEffect(() => {
    let live = true
    session
      .backupReminder()
      .then((next) => {
        if (live) {
          setReminder(next)
        }
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [session, madeCount])

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (busy) {
      return
    }
    const check = checkBackupPassphrase(value)
    if (!check.ok) {
      setProblem(backup.refusals[check.refusal])
      return
    }
    if (value !== again) {
      setAgain('')
      setProblem(backup.mismatch)
      return
    }
    const passphrase = value
    setBusy(true)
    setProblem(null)
    setMade(false)
    await nextFrame()
    try {
      const file = await session.makeBackupFile(store, passphrase)
      if (!mounted.current) {
        return
      }
      saveFile(file.bytes, file.fileName)
      await session.noteBackupSaved().catch(() => undefined)
      setValue('')
      setAgain('')
      setMade(true)
      setMadeCount((n) => n + 1)
    } catch {
      setProblem(common.tryAgainLater)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="screen">
      <form className="screen__body" onSubmit={(event) => void submit(event)} noValidate>
        <ScreenTitle>{backup.title}</ScreenTitle>
        <p className="screen__text">{backup.what}</p>
        {reminder ? <p className="notice">{backup.reminder[reminder]}</p> : null}
        <p className="screen__text">{backup.passphraseHint}</p>
        <SecretField id="backup-passphrase" label={backup.passphrase} value={value} onChange={setValue} disabled={busy} />
        <SecretField id="backup-passphrase-again" label={backup.again} value={again} onChange={setAgain} disabled={busy} />
        {problem ? (
          <p className="screen__problem" role="alert">
            {problem}
          </p>
        ) : null}
        {busy ? (
          <p className="screen__status" role="status">
            {backup.making}
          </p>
        ) : null}
        {made ? (
          <p className="screen__status" role="status">
            {backup.made}
          </p>
        ) : null}
        <div className="actions">
          <button type="submit" className="button button--primary" disabled={busy}>
            {backup.make}
          </button>
          <button type="button" className="button button--quiet" disabled={busy} onClick={onBack}>
            {common.back}
          </button>
        </div>
        <ul className="screen__list">
          {backup.honest.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </form>
    </section>
  )
}
